import { Check, ChevronDown, Palette as PaletteIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { PALETTES, paletteById } from '../lib/palette';

/**
 * The color theme dropdown (#251): a button showing the current theme that opens a grid of colorful theme cards.
 * `compact` shows just the icon and emoji (for the Student Portal header on phones).
 */
export function ThemePicker({ value, onChange, compact = false }: { value: string; onChange: (id: string) => void; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = paletteById(value);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <div className="theme-picker" ref={ref}>
      <button
        type="button"
        className={`btn ${compact ? 'small compact' : ''} theme-picker-button`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Theme: ${current.name}`}
      >
        {!compact && <PaletteIcon size={16} />}
        <span aria-hidden="true">{current.emoji}</span>
        {!compact && <span className="theme-picker-name">{current.name}</span>}
        {!compact && <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="theme-menu card" role="menu" aria-label="Choose a theme">
          <div className="theme-menu-title">Pick a theme</div>
          <ThemeGrid
            value={value}
            itemRole="menuitemradio"
            onChange={(id) => {
              onChange(id);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

/** The theme cards themselves: in the dropdown above (Student Portal) and right in Settings → Appearance (#302). */
export function ThemeGrid({ value, onChange, itemRole = 'radio' }: { value: string; onChange: (id: string) => void; itemRole?: 'radio' | 'menuitemradio' }) {
  const current = paletteById(value);
  return (
    <div className="theme-grid" role={itemRole === 'radio' ? 'radiogroup' : undefined} aria-label={itemRole === 'radio' ? 'Color theme' : undefined}>
      {PALETTES.map((p) => (
        <button
          key={p.id || 'default'}
          type="button"
          role={itemRole}
          aria-checked={p.id === current.id}
          className={`theme-card ${p.id === current.id ? 'selected' : ''}`}
          onClick={() => onChange(p.id)}
        >
          <span className="theme-swatch" style={{ background: p.swatch }}>
            {p.emoji}
          </span>
          <span className="theme-card-name">{p.name}</span>
          {p.id === current.id && <Check size={14} className="theme-check" />}
        </button>
      ))}
    </div>
  );
}
